import { useState, useEffect, useMemo } from 'react';
import { useOffline } from '../../context/OfflineContext';
import { useToast } from '../../context/ToastContext';
import { getExpenses, updateExpense, deleteExpense } from '../../services/syncService';
import { budgetsApi, tripsApi } from '../../services/api';
import { getCategoryIcon } from '../UI/Icons';
import ConfirmModal from '../UI/ConfirmModal';
import ExpenseFilters from './ExpenseFilters';
import ExpenseSummary from './ExpenseSummary';
import ExpenseRow from './ExpenseRow';
import ExpenseMobileCard from './ExpenseMobileCard';
import ExpensesSkeleton from './ExpensesSkeleton';

/**
 * ExpensesList - Main expenses list view with filtering and inline editing
 */
export default function ExpensesList() {
  const { isOnline, isReady, updatePendingCount } = useOffline();
  const toast = useToast();
  
  // Data state
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [years, setYears] = useState([]);
  const [trips, setTrips] = useState([]);
  
  // Filter state
  const [filter, setFilter] = useState({ 
    category: '', 
    is_planned: '',
    year: new Date().getFullYear().toString(),
    trip_id: '',
  });
  
  // Edit state
  const [editingExpense, setEditingExpense] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, expense: null });

  // Load years and trips on mount
  useEffect(() => {
    if (isReady && isOnline) {
      loadYearsAndTrips();
    }
  }, [isReady, isOnline]);

  async function loadYearsAndTrips() {
    try {
      const [yearsData, tripsData] = await Promise.all([
        budgetsApi.getYears(),
        tripsApi.list(),
      ]);
      setYears(yearsData.years || []);
      setTrips(tripsData || []);
    } catch (err) {
      console.error('Failed to load years/trips:', err);
    }
  }

  useEffect(() => {
    if (isReady) {
      loadExpenses();
    }
  }, [filter, isOnline, isReady]);

  async function loadExpenses() {
    try {
      setLoading(true);
      const params = {};
      if (filter.category) params.category = filter.category;
      if (filter.is_planned !== '') params.is_planned = filter.is_planned;
      if (filter.year) params.year = filter.year;
      if (filter.trip_id) params.trip_id = filter.trip_id;
      
      const data = await getExpenses(isOnline, params);
      
      // Apply filters locally for offline data
      let filtered = data;
      if (filter.category) {
        filtered = filtered.filter(e => e.category === filter.category);
      }
      if (filter.is_planned !== '') {
        const isPlanned = filter.is_planned === 'true';
        filtered = filtered.filter(e => e.is_planned === isPlanned);
      }
      setExpenses(filtered);
    } catch (err) {
      console.error('Failed to load expenses:', err);
    } finally {
      setLoading(false);
    }
  }

  // Calculate totals
  const totals = useMemo(() => {
    return expenses.reduce((acc, exp) => {
      acc.total += exp.amount;
      if (exp.is_planned) {
        acc.planned += exp.amount;
      } else {
        acc.actual += exp.amount;
      }
      return acc;
    }, { total: 0, planned: 0, actual: 0 });
  }, [expenses]);

  // Edit handlers
  function handleStartEdit(expense) {
    setEditingExpense(expense.id);
    setEditAmount(expense.amount.toString());
  }

  async function handleSaveEdit(expense) {
    if (!editAmount || isNaN(parseFloat(editAmount))) {
      setEditingExpense(null);
      return;
    }

    try {
      await updateExpense(expense.id, {
        ...expense,
        amount: parseFloat(editAmount),
      }, isOnline);
      setEditingExpense(null);
      if (!isOnline) updatePendingCount();
      toast.success('Expense updated');
      loadExpenses();
    } catch (err) {
      console.error('Failed to update expense:', err);
      toast.error('Failed to update expense');
    }
  }

  function handleCancelEdit() {
    setEditingExpense(null);
    setEditAmount('');
  }

  // Toggle planned/actual
  async function handleTogglePlanned(expense) {
    try {
      await updateExpense(expense.id, {
        ...expense,
        is_planned: !expense.is_planned,
      }, isOnline);
      if (!isOnline) updatePendingCount();
      toast.success(`Marked as ${expense.is_planned ? 'actual' : 'planned'}`);
      loadExpenses();
    } catch (err) {
      console.error('Failed to update expense:', err);
      toast.error('Failed to update expense');
    }
  }

  // Delete handlers
  function confirmDelete(expense) {
    setDeleteConfirm({ show: true, expense });
  }

  async function handleDelete(id) {
    try {
      await deleteExpense(id, isOnline);
      setDeleteConfirm({ show: false, expense: null });
      if (!isOnline) updatePendingCount();
      toast.success('Expense deleted');
      loadExpenses();
    } catch (err) {
      console.error('Failed to delete expense:', err);
      toast.error('Failed to delete expense');
    }
  }

  // Show skeleton while loading initial data
  if (loading && expenses.length === 0) {
    return <ExpensesSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Expenses
          </h2>
          <p style={{ color: 'var(--color-text-muted)' }} className="mt-1">
            View and manage all your expenses
          </p>
        </div>
      </div>

      {/* Filters & Summary */}
      <div className="flex flex-wrap items-center gap-3">
        <ExpenseFilters 
          filter={filter} 
          onChange={setFilter}
          years={years}
          trips={trips}
          isOnline={isOnline}
        />
        <div className="flex-1" />
        <ExpenseSummary totals={totals} />
      </div>

      {/* Expenses List */}
      {expenses.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="card overflow-hidden">
          {/* Desktop Table */}
          <table className="w-full hidden md:table">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border-subtle)' }}>
                <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Date</th>
                <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Description</th>
                <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Category</th>
                <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Type</th>
                <th className="text-right px-4 py-3 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Amount</th>
                <th className="text-right px-4 py-3 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(expense => (
                <ExpenseRow
                  key={expense.id}
                  expense={expense}
                  isEditing={editingExpense === expense.id}
                  editAmount={editAmount}
                  onEditAmountChange={setEditAmount}
                  onStartEdit={handleStartEdit}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={handleCancelEdit}
                  onTogglePlanned={handleTogglePlanned}
                  onDelete={confirmDelete}
                />
              ))}
            </tbody>
          </table>

          {/* Mobile List */}
          <div className="md:hidden">
            {expenses.map(expense => (
              <ExpenseMobileCard
                key={expense.id}
                expense={expense}
                isEditing={editingExpense === expense.id}
                editAmount={editAmount}
                onEditAmountChange={setEditAmount}
                onStartEdit={handleStartEdit}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onTogglePlanned={handleTogglePlanned}
                onDelete={confirmDelete}
              />
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, expense: null })}
        onConfirm={() => handleDelete(deleteConfirm.expense?.id)}
        title="Delete Expense"
        message={`Are you sure you want to delete "${deleteConfirm.expense?.description}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card">
      <div className="card-body text-center py-12">
        <div 
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3"
          style={{ backgroundColor: 'var(--color-bg-surface)' }}
        >
          {getCategoryIcon('miscellaneous', { size: 32, style: { color: 'var(--color-text-muted)' } })}
        </div>
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          No expenses yet
        </h3>
        <p style={{ color: 'var(--color-text-muted)' }} className="mt-1">
          Add expenses from the calendar by creating an event with an expense, or directly as expense-only entries.
        </p>
      </div>
    </div>
  );
}
